import { GenericSelect } from '@/components/GenericSelect';
import { checkMaterialEditInput, EditMaterialInputType } from '@shelflix/schemas/material/edit';
import { DisplayDto } from '@shelflix/trpc/routes/material/listDisplays/useCase';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface DisplayInfoSectionProps {
  form: UseFormReturn<
    EditMaterialInputType & {
      cnpj: {
        uuid: string;
        cnpj: string;
        name: string;
      };
      materialTypeUuid: {
        uuid: string;
        name: string;
        brand: {
          name: string;
        };
      };
    }
  >;
  displays: DisplayDto[];
  canEdit: boolean;
}

export function DisplayInfoSection({ form, displays, canEdit }: DisplayInfoSectionProps) {
  const canEditMaterial = canEdit;
  const code = form.watch('display.code');

  const currentDisplay = displays.find((d) => d.code === code);

  useEffect(() => {
    if (currentDisplay) {
      form.setValue('display.code', currentDisplay.code);
      // MagicInfo can omit fields; keep the stored value when the list has none
      if (currentDisplay.brand) form.setValue('display.brand', currentDisplay.brand);
      if (currentDisplay.deviceModelName) form.setValue('display.model', currentDisplay.deviceModelName);
      if (currentDisplay.screenOrientation) form.setValue('display.orientation', currentDisplay.screenOrientation);
      if (currentDisplay.deviceType) form.setValue('display.type', currentDisplay.deviceType);
      if (currentDisplay.screenSize) form.setValue('display.size', currentDisplay.screenSize);
    }
  }, [currentDisplay]);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col items-end gap-4 sm:flex-row">
        <FormField
          control={form.control}
          name="display.code"
          rules={{ validate: (value) => checkMaterialEditInput('display.code', value) }}
          render={({ field: _field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Identificador da tela</FormLabel>
              <FormControl>
                <GenericSelect<DisplayDto>
                  listOnEmptySearch
                  disabled
                  fetchItems={(search) => {
                    const items = displays.map((d) => ({
                      ...d,
                      disabled: !!d.currentMaterialAssociation,
                    }));
                    return !search
                      ? Promise.resolve(items)
                      : Promise.resolve(items.filter((d) => d.code.toLowerCase().includes(search.toLowerCase())));
                  }}
                  onSearchChange={() => {}}
                  value={currentDisplay}
                  onSelect={(item: DisplayDto) => {
                    if (item.currentMaterialAssociation) return;
                    form.setValue('display.code', item.code);
                    form.setValue('display.brand', item.brand);
                    form.setValue('display.model', item.deviceModelName);
                    form.setValue('display.orientation', item.screenOrientation);
                    form.setValue('display.type', item.deviceType);
                    form.setValue('display.size', item.screenSize);
                    form.trigger('display');
                  }}
                  renderItem={(item: DisplayDto) => (
                    <div
                      className={`flex w-full flex-col gap-2 ${item.currentMaterialAssociation ? 'cursor-disabled opacity-40' : ''}`}
                    >
                      <div className="flex w-full items-center">
                        <span>{item.code}</span>
                        <div className="ml-auto flex flex-col">
                          <Badge variant={item.status.toUpperCase() === 'ACTIVE' ? 'default' : 'destructive'}>
                            {item.status.toUpperCase() === 'ACTIVE' ? 'ONLINE' : item.status}
                          </Badge>
                        </div>
                      </div>
                      {item.currentMaterialAssociation && (
                        <div>
                          <span className="text-[10px]">
                            Dispositivo em uso em {item.currentMaterialAssociation.facilityName}
                            {item.currentMaterialAssociation.exhibitionPoint
                              ? ` (${item.currentMaterialAssociation.exhibitionPoint})`
                              : ''}{' '}
                            - {item.currentMaterialAssociation.code}
                          </span>
                        </div>
                      )}
                      {!item.currentMaterialAssociation && (
                        <div>
                          <span className="text-[13px] text-[var(--active-dark)]">Disponível</span>
                        </div>
                      )}
                    </div>
                  )}
                  getItemLabel={(item: DisplayDto) => item.code}
                  getItemKey={(item: DisplayDto) => item.code}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display.brand"
          // rules={{ validate: (value) => checkMaterialEditInput('display.brand', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca do Monitor</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Marca X" disabled={!canEditMaterial} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="display.model"
          rules={{ validate: (value) => checkMaterialEditInput('display.model', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Modelo</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Ex: LG 556UH-F" disabled={!canEditMaterial} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex w-full flex-col items-end gap-4 sm:flex-row">
        <FormField
          control={form.control}
          name="display.orientation"
          rules={{ validate: (value) => checkMaterialEditInput('display.orientation', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Orientação</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Selecione uma opção" disabled={!canEditMaterial} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem className="flex w-full flex-col gap-[3px]">
          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Device ID</FormLabel>
          <FormControl>
            <Input className="h-10" disabled value={currentDisplay?.id} />
          </FormControl>
          <FormMessage />
        </FormItem>
        <FormField
          control={form.control}
          name="display.size"
          rules={{ validate: (value) => checkMaterialEditInput('display.size', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tamanho de Tela</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Ex: 40" disabled={!canEditMaterial} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
